import { createAction, props } from '@ngrx/store';
import { Group } from '../../models/group.model';
import { HierarchyNode } from '../../models/hierarchy-node.model';
import { ManagementAssignment } from '../../models/management-assignment.model';
import { MembershipStatus } from '../../models/membership.status';
import { MembershipRequestOutcome, OrganizationMember } from '../../models/organization-member.model';
import { Organization } from '../../models/organization.model';

// My Assignments (Home Page & Navigation)
export const loadMyAssignments = createAction(
  '[Management] Load My Assignments',
  props<{ userId: string }>()
);

export const loadMyAssignmentsSuccess = createAction(
  '[Management] Load My Assignments Success',
  props<{ assignments: ManagementAssignment[] }>()
);

export const loadMyAssignmentsFailure = createAction(
  '[Management] Load My Assignments Failure',
  props<{ error: any }>()
);

// Organization Details
export const loadOrganization = createAction(
  '[Management] Load Organization',
  props<{ orgId: string }>()
);

export const loadOrganizationSuccess = createAction(
  '[Management] Load Organization Success',
  props<{ organization: Organization | null }>()
);

export const loadOrganizationFailure = createAction(
  '[Management] Load Organization Failure',
  props<{ error: any }>()
);

// Hierarchy Nodes
export const loadHierarchyNodes = createAction(
  '[Management] Load Hierarchy Nodes',
  props<{ orgId: string }>()
);

export const loadHierarchyNodesSuccess = createAction(
  '[Management] Load Hierarchy Nodes Success',
  props<{ nodes: HierarchyNode[] }>()
);

export const loadHierarchyNodesFailure = createAction(
  '[Management] Load Hierarchy Nodes Failure',
  props<{ error: any }>()
);

// Affiliated Churches
export const loadAffiliatedChurches = createAction(
  '[Management] Load Affiliated Churches',
  props<{ orgId: string }>()
);

export const loadAffiliatedChurchesSuccess = createAction(
  '[Management] Load Affiliated Churches Success',
  props<{ churches: Group[] }>()
);

export const loadAffiliatedChurchesFailure = createAction(
  '[Management] Load Affiliated Churches Failure',
  props<{ error: any }>()
);

// Node Leaders (Inspector Drawer)
export const loadNodeLeaders = createAction(
  '[Management] Load Node Leaders',
  props<{ orgId: string; nodeId: string }>()
);

export const loadNodeLeadersSuccess = createAction(
  '[Management] Load Node Leaders Success',
  props<{ leaders: ManagementAssignment[] }>()
);

export const loadNodeLeadersFailure = createAction(
  '[Management] Load Node Leaders Failure',
  props<{ error: any }>()
);

// Church Upward Oversight
export const loadChurchOversight = createAction(
  '[Management] Load Church Oversight',
  props<{ churchId: string }>()
);

export const loadChurchOversightSuccess = createAction(
  '[Management] Load Church Oversight Success',
  props<{
    organization: Organization | null;
    lineage: HierarchyNode[];
    leaders: ManagementAssignment[];
  }>()
);

export const loadChurchOversightFailure = createAction(
  '[Management] Load Church Oversight Failure',
  props<{ error: any }>()
);

// UI State
export const selectHierarchyNode = createAction(
  '[Management] Select Hierarchy Node',
  props<{ nodeId: string | null }>()
);

export const selectChurch = createAction(
  '[Management] Select Church',
  props<{ churchId: string | null }>()
);

export const setViewMode = createAction(
  '[Management] Set View Mode',
  props<{ viewMode: 'list' | 'canvas' }>()
);

// Create Hierarchy Node
/** Level (e.g. "District") and name of an org-chart unit, for user-facing messages. */
export interface NodeLabel {
  levelName?: string;
  nodeName?: string;
}

export const createHierarchyNode = createAction(
  '[Management] Create Hierarchy Node',
  props<{ orgId: string; node: Partial<HierarchyNode> }>()
);
export const createHierarchyNodeSuccess = createAction(
  '[Management] Create Hierarchy Node Success',
  props<{ orgId: string; nodeId: string } & NodeLabel>()
);
export const createHierarchyNodeFailure = createAction(
  '[Management] Create Hierarchy Node Failure',
  props<{ error: any } & NodeLabel>()
);

// Update Hierarchy Node
export const updateHierarchyNode = createAction(
  '[Management] Update Hierarchy Node',
  props<{ orgId: string; nodeId: string; updates: Partial<HierarchyNode> } & NodeLabel>()
);
export const updateHierarchyNodeSuccess = createAction(
  '[Management] Update Hierarchy Node Success',
  props<{ orgId: string; nodeId: string } & NodeLabel>()
);
export const updateHierarchyNodeFailure = createAction(
  '[Management] Update Hierarchy Node Failure',
  props<{ error: any } & NodeLabel>()
);

// Delete Hierarchy Node
export const deleteHierarchyNode = createAction(
  '[Management] Delete Hierarchy Node',
  props<{ orgId: string; nodeId: string; parentNodeId: string | null } & NodeLabel>()
);
export const deleteHierarchyNodeSuccess = createAction(
  '[Management] Delete Hierarchy Node Success',
  props<{ orgId: string; nodeId: string; parentNodeId: string | null } & NodeLabel>()
);
export const deleteHierarchyNodeFailure = createAction(
  '[Management] Delete Hierarchy Node Failure',
  props<{ error: any } & NodeLabel>()
);

// Assign & Remove Node Leader
export const assignNodeLeader = createAction(
  '[Management] Assign Node Leader',
  props<{ orgId: string; nodeId: string; assignment: Partial<ManagementAssignment> }>()
);
export const assignNodeLeaderSuccess = createAction(
  '[Management] Assign Node Leader Success',
  props<{ orgId: string; nodeId: string }>()
);
export const assignNodeLeaderFailure = createAction(
  '[Management] Assign Node Leader Failure',
  props<{ error: any }>()
);

export const removeNodeLeader = createAction(
  '[Management] Remove Node Leader',
  props<{ orgId: string; nodeId: string; assignmentId: string }>()
);
export const removeNodeLeaderSuccess = createAction(
  '[Management] Remove Node Leader Success',
  props<{ orgId: string; nodeId: string }>()
);
export const removeNodeLeaderFailure = createAction(
  '[Management] Remove Node Leader Failure',
  props<{ error: any }>()
);

// My Organizations (Left Sidebar & Navigation)
export const loadMyOrganizations = createAction(
  '[Management] Load My Organizations',
  props<{ userId: string }>()
);
export const loadMyOrganizationsSuccess = createAction(
  '[Management] Load My Organizations Success',
  props<{ organizations: Organization[] }>()
);
export const loadMyOrganizationsFailure = createAction(
  '[Management] Load My Organizations Failure',
  props<{ error: any }>()
);

// Organization Members
export const loadOrganizationMembers = createAction(
  '[Management] Load Organization Members',
  props<{ orgId: string }>()
);
export const loadOrganizationMembersSuccess = createAction(
  '[Management] Load Organization Members Success',
  props<{ members: OrganizationMember[] }>()
);
export const loadOrganizationMembersFailure = createAction(
  '[Management] Load Organization Members Failure',
  props<{ error: any }>()
);

// Invite Member
export const inviteOrganizationMember = createAction(
  '[Management] Invite Organization Member',
  props<{
    orgId: string;
    email: string;
    name: string;
    role: 'admin' | 'member';
    inviterUid: string;
  }>()
);
export const inviteOrganizationMemberSuccess = createAction(
  '[Management] Invite Organization Member Success',
  props<{ member: OrganizationMember }>()
);
export const inviteOrganizationMemberFailure = createAction(
  '[Management] Invite Organization Member Failure',
  props<{ error: any }>()
);

// Request Membership (Public Link / User Request)
export const requestOrganizationMembership = createAction(
  '[Management] Request Organization Membership',
  props<{
    orgId: string;
    user: {
      uid: string;
      name?: string;
      email?: string;
      photoURL?: string;
      contact?: any;
      address?: any;
      profession?: string;
      gender?: string;
    };
  }>()
);
export const requestOrganizationMembershipSuccess = createAction(
  '[Management] Request Organization Membership Success',
  props<{ member: OrganizationMember; outcome: MembershipRequestOutcome }>()
);
export const requestOrganizationMembershipFailure = createAction(
  '[Management] Request Organization Membership Failure',
  props<{ error: any }>()
);

// Load All Organization Assignments
export const loadOrganizationAssignments = createAction(
  '[Management] Load Organization Assignments',
  props<{ orgId: string }>()
);
export const loadOrganizationAssignmentsSuccess = createAction(
  '[Management] Load Organization Assignments Success',
  props<{ assignments: ManagementAssignment[] }>()
);
export const loadOrganizationAssignmentsFailure = createAction(
  '[Management] Load Organization Assignments Failure',
  props<{ error: any }>()
);

// Assign Member to Node Role
export const assignMemberToNodeRole = createAction(
  '[Management] Assign Member To Node Role',
  props<{
    orgId: string;
    nodeId: string;
    roleId: string;
    member: { userId: string; userName: string; userEmail: string; userPhotoUrl?: string };
  }>()
);
export const assignMemberToNodeRoleSuccess = createAction(
  '[Management] Assign Member To Node Role Success',
  props<{ orgId: string; nodeId: string }>()
);
export const assignMemberToNodeRoleFailure = createAction(
  '[Management] Assign Member To Node Role Failure',
  props<{ error: any }>()
);

// Remove Member from Node Role
export const removeMemberFromNodeRole = createAction(
  '[Management] Remove Member From Node Role',
  props<{ orgId: string; nodeId: string; roleId: string; userId: string }>()
);
export const removeMemberFromNodeRoleSuccess = createAction(
  '[Management] Remove Member From Node Role Success',
  props<{ orgId: string; nodeId: string }>()
);
export const removeMemberFromNodeRoleFailure = createAction(
  '[Management] Remove Member From Node Role Failure',
  props<{ error: any }>()
);

// Update Member Status / Role (Approve / Reject / Remove / Promote)
export const updateOrganizationMemberStatus = createAction(
  '[Management] Update Organization Member Status',
  props<{
    orgId: string;
    memberDocId: string;
    status: MembershipStatus;
    role?: 'admin' | 'member';
    approverUid?: string;
  }>()
);
export const updateOrganizationMemberStatusSuccess = createAction(
  '[Management] Update Organization Member Status Success',
  props<{
    orgId: string;
    memberDocId: string;
    status: MembershipStatus;
    role?: 'admin' | 'member';
  }>()
);
export const updateOrganizationMemberStatusFailure = createAction(
  '[Management] Update Organization Member Status Failure',
  props<{ error: any }>()
);

// Update Organization Settings, Logo, Details & Levels
export const updateOrganization = createAction(
  '[Management] Update Organization',
  props<{ orgId: string; updates: Partial<Organization> }>()
);
export const updateOrganizationSuccess = createAction(
  '[Management] Update Organization Success',
  props<{ orgId: string; updates: Partial<Organization> }>()
);
export const updateOrganizationFailure = createAction(
  '[Management] Update Organization Failure',
  props<{ error: any }>()
);

// Move Church Affiliation Jurisdiction
export const moveChurchAffiliation = createAction(
  '[Management] Move Church Affiliation',
  props<{
    orgId: string;
    churchId: string;
    targetNodeId: string;
  }>()
);
export const moveChurchAffiliationSuccess = createAction(
  '[Management] Move Church Affiliation Success',
  props<{
    orgId: string;
    churchId: string;
    targetNodeId: string;
  }>()
);
export const moveChurchAffiliationFailure = createAction(
  '[Management] Move Church Affiliation Failure',
  props<{ error: any }>()
);

